import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Coffee, Utensils, Wine } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { analyzeMicronutrients, runAiAnalysis, undoFinalizeEntry, updateFoodDescription } from '@/utils/api';
import { FoodEntryCard, FoodEntryDetailModal } from '@/components/food';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * FoodGallery - Main gallery component for displaying food entries
 * Refactored to use FoodEntryCard and FoodEntryDetailModal sub-components
 */
const FoodGallery = ({ entries, onEntryUpdate, token }) => {
  const [relabelling, setRelabelling] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editedAnalysis, setEditedAnalysis] = useState('');
  const [finalizing, setFinalizing] = useState(null);
  const [undoingFinalization, setUndoingFinalization] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [analyzingMicros, setAnalyzingMicros] = useState(false);
  const [runningAiAnalysis, setRunningAiAnalysis] = useState(null);
  
  // Micronutrient reminder popup state
  const [showMicronutrientReminder, setShowMicronutrientReminder] = useState(false);
  
  // Check if entry is close to auto-finalization
  const isNearAutoFinalization = (entry) => {
    if (entry.is_finalized) return false;
    const created = new Date(entry.finalization_undone_at || entry.created_at);
    const now = new Date();
    const hours = (now - created) / (1000 * 60 * 60);
    const threshold = entry.finalization_undone_at ? 24 : 48;
    return hours >= (threshold - 12);
  };

  // === Event Handlers ===

  const handleRelabel = async (entryId, newMealType) => {
    setRelabelling(entryId);
    try {
      await axios.patch(
        `${API_URL}/api/food-entries/${entryId}/relabel`,
        { meal_type: newMealType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Meal type updated!');
      if (onEntryUpdate) onEntryUpdate();
    } catch (error) {
      toast.error('Failed to update meal type');
    } finally {
      setRelabelling(null);
    }
  };

  const handleEditAnalysis = (entry) => {
    setEditing(entry.id);
    setEditedAnalysis(entry.analysis || '');
  };

  const handleSaveAnalysis = async (entryId) => {
    try {
      await updateFoodDescription(entryId, editedAnalysis, token);
      toast.success('Description updated!');
      setEditing(null);
      if (onEntryUpdate) onEntryUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update description');
    }
  };

  const handleFinalizeEntry = async (entryId) => {
    setFinalizing(entryId);
    try {
      await axios.post(
        `${API_URL}/api/food-entries/${entryId}/finalize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Entry finalized with macro analysis!');
      setShowMicronutrientReminder(true);
      if (onEntryUpdate) onEntryUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize entry');
    } finally {
      setFinalizing(null);
    }
  };

  const handleUndoFinalization = async (entryId) => {
    setUndoingFinalization(entryId);
    try {
      await undoFinalizeEntry(entryId, token);
      toast.success('Finalization undone. You can now edit the description.');
      if (onEntryUpdate) onEntryUpdate();
      setSelectedEntry(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo finalization');
    } finally {
      setUndoingFinalization(null);
    }
  };

  const handleEntryClick = async (entry) => {
    setSelectedEntry(entry);
    if (entry.is_finalized && !entry.micros_analyzed) {
      setAnalyzingMicros(true);
      try {
        const updatedEntry = await analyzeMicronutrients(entry.id, token);
        setSelectedEntry(updatedEntry);
        if (onEntryUpdate) onEntryUpdate();
        toast.success('Micronutrient analysis complete!');
      } catch (error) {
        toast.error('Failed to analyze micronutrients');
      } finally {
        setAnalyzingMicros(false);
      }
    }
  };

  const closeModal = () => {
    setSelectedEntry(null);
    setAnalyzingMicros(false);
  };

  const handleRunAiAnalysis = async (entryId) => {
    setRunningAiAnalysis(entryId);
    try {
      const updatedEntry = await runAiAnalysis(entryId, token);
      toast.success('AI analysis complete!');
      if (onEntryUpdate) onEntryUpdate();
      if (selectedEntry && selectedEntry.id === entryId) {
        setSelectedEntry(updatedEntry);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 503 && detail?.code === 'ai_unavailable') {
        toast.error(detail.message, { id: `ai-unavailable-${entryId}`, duration: 8000,
          action: { label: 'Try again', onClick: () => handleRunAiAnalysis(entryId) } });
        if (onEntryUpdate) onEntryUpdate();
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to run AI analysis');
      }
    } finally {
      setRunningAiAnalysis(null);
    }
  };

  const handleAnalyzeMicronutrients = async (entryId) => {
    setAnalyzingMicros(true);
    try {
      const updatedEntry = await analyzeMicronutrients(entryId, token);
      setSelectedEntry(updatedEntry);
      if (onEntryUpdate) onEntryUpdate();
      toast.success('Micronutrient analysis complete!');
    } catch (error) {
      toast.error('Failed to analyze micronutrients');
    } finally {
      setAnalyzingMicros(false);
    }
  };

  // === Helper Functions ===

  const formatDayHeader = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  // Group entries by date
  const groupedEntries = entries.reduce((groups, entry) => {
    const date = new Date(entry.created_at).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(entry);
    return groups;
  }, {});

  // Sort dates in descending order
  const sortedDates = Object.keys(groupedEntries).sort((a, b) => 
    new Date(b) - new Date(a)
  );

  // === Render ===

  return (
    <div className="space-y-12">
      {/* Detail Modal */}
      <AnimatePresence>
        {selectedEntry && (
          <FoodEntryDetailModal
            entry={selectedEntry}
            onClose={closeModal}
            onRelabel={handleRelabel}
            onRunAiAnalysis={handleRunAiAnalysis}
            onUndoFinalize={handleUndoFinalization}
            onAnalyzeMicronutrients={handleAnalyzeMicronutrients}
            relabelling={relabelling}
            runningAiAnalysis={runningAiAnalysis}
            undoingFinalization={undoingFinalization}
            analyzingMicros={analyzingMicros}
            showMicronutrientReminder={showMicronutrientReminder}
            setShowMicronutrientReminder={setShowMicronutrientReminder}
          />
        )}
      </AnimatePresence>

      {/* Entries grouped by date */}
      {sortedDates.map((date, dateIndex) => (
        <div key={date} className="space-y-6">
          {/* Day header */}
          <div className="flex items-center gap-4 pb-4 border-b border-border">
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {formatDayHeader(groupedEntries[date][0].created_at)}
            </h2>
            <span className="text-sm text-muted-foreground ml-auto">
              {groupedEntries[date].length} {groupedEntries[date].length === 1 ? 'item' : 'items'}
            </span>
          </div>

          {/* Entry cards grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {groupedEntries[date].map((entry, index) => (
              <FoodEntryCard
                key={entry.id}
                entry={entry}
                index={index}
                dateIndex={dateIndex}
                editing={editing}
                editedAnalysis={editedAnalysis}
                setEditedAnalysis={setEditedAnalysis}
                finalizing={finalizing}
                runningAiAnalysis={runningAiAnalysis}
                relabelling={relabelling}
                undoingFinalization={undoingFinalization}
                onEntryClick={handleEntryClick}
                onEditAnalysis={handleEditAnalysis}
                onSaveAnalysis={handleSaveAnalysis}
                onCancelEdit={() => setEditing(null)}
                onFinalizeEntry={handleFinalizeEntry}
                onRunAiAnalysis={handleRunAiAnalysis}
                onRelabel={handleRelabel}
                onUndoFinalize={handleUndoFinalization}
                onDeleteEntry={() => { if (onEntryUpdate) onEntryUpdate(); }}
                isNearAutoFinalization={isNearAutoFinalization}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Empty state */}
      {entries.length === 0 && (
        <div className="text-center py-16">
          <p className="text-muted-foreground text-lg">No food entries yet</p>
          <p className="text-sm text-muted-foreground mt-2">
            Start tracking your meals by uploading photos
          </p>
        </div>
      )}
    </div>
  );
};

export default FoodGallery;
